import React from "react";
import { ScanReportType } from "@sscs/types";
import { Button, Page } from "@sscs/ui";
import { buildRlPurl, getClassName } from "@sscs/utils";
import { useAppDispatch, useAppSelector } from "Hooks";
import { ReportExportButton } from "Modules/download";
import { useGetVersionQuery } from "Services";
import { SharedReportStats } from "Types";
import {
  closeShareReport,
  openShareReport,
  selectShareReportDialog,
} from "../../store/dialogs.slice";
import { PurlComponents } from "../../types";
import LinksAndStatistics from "./LinksAndStatistics/LinksAndStatistics";
import styles from "./projectReportHeader.module.scss";
import ShareReportDialog from "./ShareReportDialog/ShareReportDialog";
import SharingStatusText from "./SharingStatusText/SharingStatusText";

type ProjectReportHeaderProps = {
  versionId: string;
  purlComponents: PurlComponents;
  reportStats: SharedReportStats | null;
  reportType: ScanReportType;
  canGenerateReportLink: boolean;
};

function ProjectReportHeader({
  versionId,
  purlComponents,
  reportStats,
  reportType,
  canGenerateReportLink,
}: ProjectReportHeaderProps): React.JSX.Element | null {
  const dispatch = useAppDispatch();
  const shareReportDialog = useAppSelector(selectShareReportDialog);
  const { data } = useGetVersionQuery({ versionId });

  const purl = buildRlPurl(
    purlComponents.projectName,
    purlComponents.packageName,
    purlComponents.version,
    reportType,
  );
  const timesShared = reportStats?.number_of_times_shared ?? 0;
  const hasBeenShared = timesShared > 0;

  return (
    <Page.ExpandableHeader>
      {shareReportDialog.isOpen && (
        <ShareReportDialog
          purl={purl}
          versionId={versionId}
          reportType={reportType}
          onClose={() => dispatch(closeShareReport())}
          isOpen={shareReportDialog.isOpen}
          contactSupport={shareReportDialog.contactSupport}
        />
      )}
      <Page.ExpandableHeader.Summary
        expandLabel={hasBeenShared ? "Shared links" : undefined}
      >
        <SharingStatusText
          projectId={data?.project_id ?? ""}
          packageId={data?.package_id ?? ""}
          purlComponents={purlComponents}
          reportType={reportType}
          timesShared={timesShared}
          user={data?.user ?? null}
          importedFrom={data?.imported_from ?? null}
        />
        <div
          className={getClassName(styles.actions, {
            [styles["actions-expandable"]]: hasBeenShared,
          })}
        >
          <ReportExportButton
            key={1}
            versionId={versionId}
            isRepro={reportType === "repro"}
            fileType="VERSION"
          />
          {!hasBeenShared && canGenerateReportLink && (
            <Button
              key={2}
              type="button"
              color="primary"
              onClick={() =>
                dispatch(openShareReport({ contactSupport: false }))
              }
            >
              Share report
            </Button>
          )}
        </div>
      </Page.ExpandableHeader.Summary>
      <Page.ExpandableHeader.Content>
        <LinksAndStatistics
          versionId={versionId}
          purl={purl}
          reportType={reportType}
          stats={reportStats}
          openShareReportDialog={() =>
            dispatch(openShareReport({ contactSupport: false }))
          }
        />
      </Page.ExpandableHeader.Content>
    </Page.ExpandableHeader>
  );
}

export default ProjectReportHeader;
